import { Link as RouterLink } from 'react-router-dom';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Chip, formatStatusLabel, Link, resolveStatusChipTone, Surface } from '@/ui/primitives';
import type { SuiteDetailDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type SuiteDetailsPanelsProps = {
  suite: SuiteDetailDto;
};

export const SuiteDetailsPanels = ({ suite }: SuiteDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const projectLabel = suite.project?.name ?? suite.project?.slug ?? suite.projectId;
  const latestExecution = suite.latestExecution ?? null;
  const latestRunLabel =
    latestExecution?.runNumber !== null && latestExecution?.runNumber !== undefined
      ? `#${latestExecution.runNumber}`
      : noDataLabel;
  const latestExecutionHref =
    suite.slug && latestExecution?.runNumber
      ? `/suites/${suite.slug}/runs/${latestExecution.runNumber}`
      : null;
  const latestTimestampLabel = latestExecution
    ? formatDate(latestExecution.timestamp, { includeTime: true }) || noDataLabel
    : noDataLabel;
  const latestDurationLabel =
    latestExecution?.durationMs !== null && latestExecution?.durationMs !== undefined
      ? formatDuration(latestExecution.durationMs)
      : noDataLabel;
  const latestStatusLabel = latestExecution?.status
    ? formatStatusLabel(latestExecution.status)
    : noDataLabel;
  const tagsLabel = formatEntityTagsLabel(suite.tags);

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField label="Suite" value={suite.name} gap="none" />
              <InfoField label="Project" value={projectLabel} gap="none" />
              <InfoField label="Tags" value={tagsLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField
                label="Latest Run"
                value={
                  latestExecutionHref ? (
                    <Link
                      component={RouterLink}
                      to={latestExecutionHref}
                      underline="hover"
                      color="inherit"
                    >
                      {latestRunLabel}
                    </Link>
                  ) : (
                    latestRunLabel
                  )
                }
                gap="none"
              />
              <InfoField label="Latest Timestamp" value={latestTimestampLabel} gap="none" />
              <InfoField label="Latest Duration" value={latestDurationLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem xs={12} sm={4}>
            <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
              <InfoField
                label="Latest Status"
                value={
                  latestExecution?.status ? (
                    <Chip
                      label={latestStatusLabel}
                      tone={resolveStatusChipTone(latestExecution.status)}
                      size="xs"
                      transform="uppercase"
                    />
                  ) : (
                    latestStatusLabel
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
